import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";

import Skills from "../components/Skills";
import CircularText from "../components/CircularText";
import ProfileImg from "../assets/profile-img.png";
import FancyButton from "../components/FancyButton";
import WorkCard from "../components/WorkCard";
import usePageMeta from "../hooks/usePageMeta";
import { allProjects, products } from "../data/projects";

import Company1 from "../assets/images/company-1.png";
import Company2 from "../assets/images/company-2.png";
import Company3 from "../assets/images/company-3.png";
import Company4 from "../assets/images/company-4.png";

import "./styles.css";
import "./mobile-styles.css";

import Space from "./../components/Space";

export default function Home() {
	usePageMeta();

	return (
		<main id="main-content" tabIndex={-1}>
			<Hero />
			<Skills />
			<AboutSection />
			<FeaturedWork />
			<Testimonials />
		</main>
	);
}

function Hero() {
	const [isPageLoaded, setIsPageLoaded] = useState(false);

	const variants = {
		hidden: { opacity: 0, y: 100 },
		visible: { opacity: 1, y: 0 },
	};

	useEffect(() => {
		const timeoutId = setTimeout(() => setIsPageLoaded(true), 100);
		return () => clearTimeout(timeoutId);
	}, []);

	const state = isPageLoaded ? "visible" : "hidden";

	return (
		<div className="hero">
			<div className="hero-content">
				<motion.h1
					variants={variants}
					initial="hidden"
					animate={state}
					transition={{ ease: "easeOut", duration: 0.4 }}
				>
					Product <br /> Engineer
				</motion.h1>
				<motion.div
					className="hero-intro"
					variants={variants}
					initial="hidden"
					animate={state}
					transition={{ ease: "easeOut", duration: 0.7 }}
				>
					<p className="sm black">
						I design and build digital products, combining a
						background in UX with a curiosity for making things
						people can <span className="fancy">use.</span>
					</p>
					<div className="hero-actions">
						<FancyButton label="See my work" linkTo="/work" black />
						<FancyButton label="About me" linkTo="/about" />
					</div>
				</motion.div>
			</div>
		</div>
	);
}

function AboutSection() {
	return (
		<section className="about-me-section" aria-label="How I work">
			<div className="avatar">
				<CircularText />
				<img
					className="profile-image"
					src={ProfileImg}
					alt="Portrait of Muhammad Munaf"
					style={{
						width: 112,
					}}
				/>
			</div>

			<div className="content">
				<p className="large black">
					My design experience shapes how I build: understanding the
					problem, making the experience clear, and refining it
					through feedback.
				</p>
			</div>
		</section>
	);
}

function FeaturedWork() {
	const { ref, inView } = useInView({
		triggerOnce: true,
		threshold: 0.1,
	});

	const variants = {
		hidden: { opacity: 0, y: 100 },
		visible: { opacity: 1, y: 0 },
	};

	return (
		<section className="featured-work" aria-labelledby="featured-work-title">
			<motion.h2
				id="featured-work-title"
				ref={ref}
				initial="hidden"
				animate={inView ? "visible" : "hidden"}
				variants={variants}
				transition={{ duration: 0.6 }}
			>
				{products.length > 0 ? "Products & Work" : "Selected Work"}
			</motion.h2>

			{allProjects.map((project, index) => (
				<WorkCard key={project.slug} project={project} left={index % 2 === 1} />
			))}

			<FancyButton
				label="View portfolio deck (PDF)"
				linkTo="/munaf-portfolio-deck.pdf"
				external
				ariaLabel="View my portfolio deck, PDF, 12 MB (opens in a new tab)"
			/>
		</section>
	);
}

function Testimonials() {
	function TestimonialCard({ text, name, title, img }) {
		const { ref, inView } = useInView({
			threshold: 0.1,
			triggerOnce: true,
		});

		return (
			<motion.figure
				className="testimonial-card"
				ref={ref}
				initial={{ opacity: 0, y: 50 }}
				animate={{ opacity: inView ? 1 : 0, y: inView ? 0 : 100 }}
				transition={{ ease: "easeOut", duration: 0.6 }}
			>
				<blockquote>
					<p className="xs white" style={{ width: "100%" }}>
						{text}
					</p>
				</blockquote>
				<figcaption className="testimonial-footer">
					{img && <img src={img} alt="" />}

					<Space direction="column" gap="0.1rem">
						<span className="name">{name}</span>
						<span className="title">{title}</span>
					</Space>
				</figcaption>
			</motion.figure>
		);
	}

	return (
		<section className="testimonial-section" aria-label="Companies and testimonials">
			<div className="companies">
				<img src={Company1} alt="The Focus Group" />
				<img src={Company2} alt="Immutus" />
				<img src={Company3} alt="Jobe Systems" />
				<img src={Company4} alt="VenturePark" />
			</div>

			<div className="testimonial-slider">
				<TestimonialCard
					text="“Working with Munaf was a game-changer for our project. His unique blend of technical expertise and creative insight helped us transform our ideas into a user-friendly and aesthetically pleasing product. His designs not only look great but are intuitive and engaging, enhancing the overall user experience.”"
					name="Denis"
					title="CEO, Avanti"
				/>
			</div>
		</section>
	);
}
